import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataExport } from './queryKeys';
import { type DataExportResultRequested } from './types';
import { dataExportResultRequestedSchema } from './validations';

/**
 * Get export job status and result by job_id.
 *
 * This endpoint queries the database directly for the export job and generates
 * a SAS URL if the job is completed.
 *
 * @param {UUID} jobId - ID of the export job to query
 * @param {string} [requestId] - Optional request ID for tracking
 * @returns {Promise<DataExportResultRequested>} The result containing job_id, status, and optional sas_url
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Export/operation/get_export_result_data_export_result__job_id__get
 */
export const fetchExportResult = async (
  jobId: UUID,
  requestId?: string,
): Promise<DataExportResultRequested> => {
  const searchParams = requestId ? { request_id: requestId } : undefined;

  const response = await vestigasApi
    .get(ENDPOINT.DATA_EXPORT.GET_RESULT(jobId), {
      searchParams,
      validate: { response: dataExportResultRequestedSchema },
    })
    .json<DataExportResultRequested>();

  return response;
};

/**
 * React Query custom hook for fetching export job status and result.
 *
 * @param {UUID} jobId - The ID of the export job
 * @param {string} [requestId] - Optional request ID for tracking
 * @param {Object} options - Additional options for the useQuery hook
 * @returns {UseQueryResult<DataExportResultRequested>} The result of the useQuery hook
 */
export const useQueryExportResult = (
  jobId: UUID,
  requestId?: string,
  options?: Omit<
    UseQueryOptions<DataExportResultRequested>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    enabled: Boolean(jobId),
    queryFn: async () => fetchExportResult(jobId, requestId),
    queryKey: queryKeysDataExport.getResult(jobId, requestId),
    ...options,
  });
};
